import { useEffect, useMemo, useRef, useState } from 'react'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { Link, router, usePage } from '@inertiajs/react'
import { Download, SlidersHorizontal, X } from 'lucide-react'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  reportsDeletedHistoryCsvPath,
  reportsDeletedHistoryPath,
  reportsDeletedHistoryPdfPath,
} from '@/shared/lib/routes'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Label } from '@/shared/ui/label'
import { Input } from '@/shared/ui/input'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { MultiSelectCombobox } from '@/shared/ui/multi-select-combobox'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/shared/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import type WebDeletedHistory from '@/shared/types/web/WebDeletedHistory'
import type { SharedProps } from '@/shared/types/SharedProps'
import { formatTenantDateTimeWithZone } from '@/shared/lib/tenantDateTime'
import {
  buildOrgCascadeChangeHandlers,
  orgSelectionCsvParams,
  visibleDepartmentOptionsForSites,
  visibleUnitOptionsForParents,
} from '@/shared/lib/orgMultiSelectCascade'
import { useHydrateLocationFilters } from '@/shared/hooks/useHydrateLocationFilters'
import { clearLocationFilters, writeLocationFilters } from '@/shared/lib/locationFiltersSession'
import { ReportEmptyState } from '../components/ReportEmptyState'
import { ReportMobileField } from '../components/ReportMobileField'
import { ReportRangeOrderError } from '../components/ReportRangeOrderError'
import { isReportRangeOrderInvalid } from '@/shared/lib/report-range-order'
import { downloadExport } from '@/shared/lib/downloadExport'

const ALL_SENTINEL = 'all'

const parsePositiveIds = (value: number[] | undefined): number[] =>
  (value ?? []).map((id) => Number(id)).filter((id) => id > 0)

interface IdNameOption {
  id: number
  name: string
}

interface OrgOption {
  value: number
  label: string
  siteId?: number
  departmentId?: number
}

interface FiltersShape {
  fromDate?: string
  toDate?: string
  siteIds?: number[]
  departmentIds?: number[]
  unitIds?: number[]
  searchText?: string
  instrumentId?: number
  recordTypes?: string[]
}

interface Props {
  records: WebDeletedHistory[]
  hasMore: boolean
  nextOffset: number
  filters: FiltersShape
  instrumentOptions: IdNameOption[]
  siteOptions: OrgOption[]
  departmentOptions: OrgOption[]
  unitOptions: OrgOption[]
  [key: string]: unknown
}

const RECORD_TYPE_BADGE_VARIANTS: Record<string, string> = {
  instrument: 'bg-blue-100 text-blue-700',
  task: 'bg-purple-100 text-purple-700',
  task_log: 'bg-orange-100 text-orange-700',
  unknown: 'bg-gray-100 text-gray-600',
}

const RecordTypeBadge = ({ recordType }: { recordType: string }) => {
  const label = t(`reports.deleted_history.recordType_${recordType}` as Parameters<typeof t>[0])
  const colorClass = RECORD_TYPE_BADGE_VARIANTS[recordType] ?? RECORD_TYPE_BADGE_VARIANTS.unknown

  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}
    >
      {label}
    </span>
  )
}

const DeletedHistoryPage = () => {
  const {
    records,
    hasMore,
    nextOffset,
    filters,
    instrumentOptions,
    siteOptions,
    departmentOptions,
    unitOptions,
    tenantTimeFormat,
    currentTenantId,
    currentUserInfo,
  } = usePage<Props & SharedProps>().props
  const currentUserId = currentUserInfo?.id ?? null

  const formatDatetime = (localString: string | null, zoneAbbreviation: string | null) => {
    if (!localString) return '—'
    return formatTenantDateTimeWithZone(localString, zoneAbbreviation, tenantTimeFormat)
  }

  useBreadcrumbs([{ label: t('nav.deletedHistory') }])

  const [displayedRecords, setDisplayedRecords] = useState<WebDeletedHistory[]>(records)
  const [currentHasMore, setCurrentHasMore] = useState(hasMore)
  const [currentNextOffset, setCurrentNextOffset] = useState(nextOffset)

  const [fromDate, setFromDate] = useState(filters.fromDate ?? '')
  const [toDate, setToDate] = useState(filters.toDate ?? '')
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(() =>
    parsePositiveIds(filters.siteIds),
  )
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<number[]>(() =>
    parsePositiveIds(filters.departmentIds),
  )
  const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>(() =>
    parsePositiveIds(filters.unitIds),
  )
  const [searchText, setSearchText] = useState(filters.searchText ?? '')
  const [instrumentId, setInstrumentId] = useState(
    filters.instrumentId ? String(filters.instrumentId) : ALL_SENTINEL,
  )
  const [selectedRecordTypes, setSelectedRecordTypes] = useState<string[]>(
    filters.recordTypes ?? [],
  )
  const sheetOpenRef = useRef(false)
  const { beginVisit } = useFilterVisitGuard()

  /* eslint-disable react-hooks/set-state-in-effect -- mirror URL-driven Inertia props (camelCase) */
  useEffect(() => {
    setFromDate(filters.fromDate ?? '')
    setToDate(filters.toDate ?? '')
    setSelectedSiteIds(parsePositiveIds(filters.siteIds))
    setSelectedDepartmentIds(parsePositiveIds(filters.departmentIds))
    setSelectedUnitIds(parsePositiveIds(filters.unitIds))
    setSearchText(filters.searchText ?? '')
    setInstrumentId(filters.instrumentId ? String(filters.instrumentId) : ALL_SENTINEL)
    setSelectedRecordTypes(filters.recordTypes ?? [])
  }, [
    filters.fromDate,
    filters.toDate,
    filters.siteIds,
    filters.departmentIds,
    filters.unitIds,
    filters.searchText,
    filters.instrumentId,
    filters.recordTypes,
  ])
  /* eslint-enable react-hooks/set-state-in-effect */

  const dateRangeInvalid = isReportRangeOrderInvalid(fromDate, toDate)
  // REBUILD-557: the date range is the required export filter. Export is blocked until at least
  // one bound is set, so a click never streams a PDF/CSV of every deleted row.
  const dateFilterMissing = !fromDate && !toDate
  const [showExportFilterRequired, setShowExportFilterRequired] = useState(false)

  const syncListStateFromServer = (page: { props: Props }) => {
    const { records: nextRecords, hasMore: nextHasMore, nextOffset: nextNextOffset } = page.props
    setDisplayedRecords(nextRecords)
    setCurrentHasMore(nextHasMore)
    setCurrentNextOffset(nextNextOffset)
  }

  const buildFilterParams = (overrides: Record<string, string | undefined> = {}) => {
    const params: Record<string, string> = {}
    if (selectedSiteIds.length > 0) params.site_ids = selectedSiteIds.join(',')
    if (selectedDepartmentIds.length > 0) params.department_ids = selectedDepartmentIds.join(',')
    if (selectedUnitIds.length > 0) params.unit_ids = selectedUnitIds.join(',')
    if (fromDate) params.from_date = fromDate
    if (toDate) params.to_date = toDate
    if (searchText.trim()) params.search_text = searchText.trim()
    if (instrumentId !== ALL_SENTINEL) params.instrument_id = instrumentId
    if (selectedRecordTypes.length > 0) params.record_types = selectedRecordTypes.join(',')
    Object.entries(overrides).forEach(([key, val]) => {
      if (val != null && val !== '') {
        params[key] = val
      } else {
        delete params[key]
      }
    })
    return params
  }

  const navigate = (
    overrides: Record<string, string | undefined> = {},
    mode: FilterVisitMode = 'default',
  ) => {
    const fd = overrides.from_date ?? fromDate
    const td = overrides.to_date ?? toDate
    if (isReportRangeOrderInvalid(fd, td)) return
    // Persist effective location filters: if the override key is present (even undefined =
    // cleared), use it; otherwise fall back to current state (non-location navigate call).
    const parseCsvIds = (csv: string | undefined): number[] =>
      csv
        ? csv
            .split(',')
            .map(Number)
            .filter((id) => Number.isInteger(id) && id > 0)
        : []
    writeLocationFilters(currentTenantId, currentUserId, {
      siteIds: 'site_ids' in overrides ? parseCsvIds(overrides.site_ids) : selectedSiteIds,
      departmentIds:
        'department_ids' in overrides
          ? parseCsvIds(overrides.department_ids)
          : selectedDepartmentIds,
      unitIds: 'unit_ids' in overrides ? parseCsvIds(overrides.unit_ids) : selectedUnitIds,
    })
    const params = buildFilterParams(overrides)
    const visit = beginVisit(
      JSON.stringify(params),
      resolveFilterVisitMode(sheetOpenRef.current, mode),
    )
    if (!visit) return
    router.get(reportsDeletedHistoryPath(), params, {
      preserveState: visit.keepDropdownOpen,
      ...(visit.keepDropdownOpen ? { preserveScroll: true } : {}),
      onSuccess: (page) => {
        if (!visit.isCurrent()) return
        syncListStateFromServer(page as unknown as { props: Props })
      },
    })
  }

  const handleFromDateChange = (val: string) => {
    setFromDate(val)
    if (val) setShowExportFilterRequired(false)
    navigate({ from_date: val })
  }

  const handleToDateChange = (val: string) => {
    setToDate(val)
    if (val) setShowExportFilterRequired(false)
    navigate({ to_date: val })
  }

  const filteredDepartmentOptions = useMemo(
    () => visibleDepartmentOptionsForSites(departmentOptions ?? [], selectedSiteIds),
    [departmentOptions, selectedSiteIds],
  )

  const filteredUnitOptions = useMemo(
    () => visibleUnitOptionsForParents(unitOptions ?? [], selectedSiteIds, selectedDepartmentIds),
    [unitOptions, selectedSiteIds, selectedDepartmentIds],
  )

  const orgCascade = buildOrgCascadeChangeHandlers({
    departmentOptions: departmentOptions ?? [],
    unitOptions: unitOptions ?? [],
    selection: {
      siteIds: selectedSiteIds,
      departmentIds: selectedDepartmentIds,
      unitIds: selectedUnitIds,
    },
    applySelection: ({ siteIds, departmentIds, unitIds }) => {
      setSelectedSiteIds(siteIds)
      setSelectedDepartmentIds(departmentIds)
      setSelectedUnitIds(unitIds)
    },
  })

  const handleSiteIdsCommit = (selected: number[]) =>
    navigate(orgSelectionCsvParams(orgCascade.changeSites(selected)), 'commit')

  const handleDepartmentIdsCommit = (selected: number[]) =>
    navigate(orgSelectionCsvParams(orgCascade.changeDepartments(selected)), 'commit')

  const closeSheetWithCascadePrune = () =>
    navigate(orgSelectionCsvParams(orgCascade.pruneSelection()), 'sheet-close')

  useHydrateLocationFilters({
    tenantId: currentTenantId,
    userId: currentUserId,
    hasUrlLocationParams:
      (filters.siteIds?.length ?? 0) > 0 ||
      (filters.departmentIds?.length ?? 0) > 0 ||
      (filters.unitIds?.length ?? 0) > 0,
    currentFilters: {
      siteIds: (filters.siteIds ?? []).map((id) => Number(id)).filter((id) => id > 0),
      departmentIds: (filters.departmentIds ?? []).map((id) => Number(id)).filter((id) => id > 0),
      unitIds: (filters.unitIds ?? []).map((id) => Number(id)).filter((id) => id > 0),
    },
    siteOptions,
    departmentOptions,
    unitOptions,
    onHydrate: (hydrated) => {
      setSelectedSiteIds(hydrated.siteIds)
      setSelectedDepartmentIds(hydrated.departmentIds)
      setSelectedUnitIds(hydrated.unitIds)
      navigate(
        orgSelectionCsvParams({
          siteIds: hydrated.siteIds,
          departmentIds: hydrated.departmentIds,
          unitIds: hydrated.unitIds,
        }),
      )
    },
  })

  const handleUnitIdsCommit = (selected: number[]) => {
    setSelectedUnitIds(selected)
    navigate({ unit_ids: selected.length > 0 ? selected.join(',') : undefined }, 'commit')
  }

  const handleSearchChange = (value: string) => {
    if (value.trim() === '') {
      setSearchText('')
      navigate({ search_text: undefined })
      return
    }
    setSearchText(value)
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') navigate({ search_text: searchText.trim() || undefined })
  }

  const handleSearchBlur = () => {
    navigate({ search_text: searchText.trim() || undefined })
  }

  const handleInstrumentChange = (value: string) => {
    setInstrumentId(value)
    navigate({ instrument_id: value !== ALL_SENTINEL ? value : undefined })
  }

  const handleRecordTypesCommit = (types: string[]) => {
    setSelectedRecordTypes(types)
    navigate({ record_types: types.length > 0 ? types.join(',') : undefined }, 'commit')
  }

  const clearFilters = () => {
    setFromDate('')
    setToDate('')
    setSelectedSiteIds([])
    setSelectedDepartmentIds([])
    setSelectedUnitIds([])
    setSearchText('')
    setInstrumentId(ALL_SENTINEL)
    setSelectedRecordTypes([])
    clearLocationFilters(currentTenantId, currentUserId)
    router.get(
      reportsDeletedHistoryPath(),
      {},
      {
        preserveState: false,
        onSuccess: (page) => syncListStateFromServer(page as unknown as { props: Props }),
      },
    )
  }

  const handleLoadMore = () => {
    if (dateRangeInvalid) return
    router.get(
      reportsDeletedHistoryPath(),
      { ...buildFilterParams(), offset: String(currentNextOffset) },
      {
        preserveScroll: true,
        preserveState: true,
        only: ['records', 'hasMore', 'nextOffset'],
        onSuccess: (page) => {
          const newRecords = page.props.records as WebDeletedHistory[]
          setDisplayedRecords((previous) => [...previous, ...newRecords])
          setCurrentHasMore(page.props.hasMore as boolean)
          setCurrentNextOffset(page.props.nextOffset as number)

          // Strip the offset from the URL so a browser refresh restores the
          // default first page (5 most recent records) rather than just the
          // last-loaded slice. The offset is only meaningful for the Load More
          // fetch itself and should never be part of the shareable URL state.
          const filterSearch = new URLSearchParams(buildFilterParams()).toString()
          const cleanUrl = reportsDeletedHistoryPath() + (filterSearch ? `?${filterSearch}` : '')
          window.history.replaceState(window.history.state, '', cleanUrl)
        },
      },
    )
  }

  const handleExportCsv = () => {
    if (dateRangeInvalid) return
    if (dateFilterMissing) {
      // Stay on the page and tell the user to apply a filter (REBUILD-557).
      setShowExportFilterRequired(true)
      return
    }
    const params = buildFilterParams()
    const queryString = new URLSearchParams(params).toString()
    void downloadExport(
      `${reportsDeletedHistoryCsvPath()}${queryString ? `?${queryString}` : ''}`,
      'export.csv',
    )
  }

  const handleExportPdf = () => {
    if (dateRangeInvalid) return
    if (dateFilterMissing) {
      // Stay on the page and tell the user to apply a filter (REBUILD-557).
      setShowExportFilterRequired(true)
      return
    }
    const params = buildFilterParams()
    const queryString = new URLSearchParams(params).toString()
    void downloadExport(
      `${reportsDeletedHistoryPdfPath()}${queryString ? `?${queryString}` : ''}`,
      'export.pdf',
    )
  }

  const recordTypeOptions = [
    { value: 'task_log', label: t('reports.deleted_history.recordType_task_log') },
    { value: 'task', label: t('reports.deleted_history.recordType_task') },
    { value: 'instrument', label: t('reports.deleted_history.recordType_instrument') },
  ]

  const activeFilterCount = [
    Boolean(searchText.trim()),
    Boolean(fromDate),
    Boolean(toDate),
    selectedSiteIds.length > 0,
    selectedDepartmentIds.length > 0,
    selectedUnitIds.length > 0,
    instrumentId !== ALL_SENTINEL,
    selectedRecordTypes.length > 0,
  ].filter(Boolean).length

  const filterFields = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-start">
      <div className="flex flex-col gap-1 sm:col-span-2 lg:col-span-1 lg:min-w-[12rem]">
        <Label className="text-xs text-muted-foreground">
          {t('reports.deleted_history.filterSearch')}
        </Label>
        <Input
          value={searchText}
          onChange={(event) => handleSearchChange(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          onBlur={handleSearchBlur}
          placeholder={t('reports.deleted_history.filterSearchPlaceholder')}
          className="w-full lg:w-52"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.deleted_history.filterSite')}
        </Label>
        <MultiSelectCombobox
          options={(siteOptions ?? []).map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedSiteIds}
          onChange={orgCascade.changeSites}
          onClose={handleSiteIdsCommit}
          placeholder={t('reports.deleted_history.filterSitePlaceholder')}
          className="w-full lg:w-44"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.deleted_history.filterDepartment')}
        </Label>
        <MultiSelectCombobox
          options={filteredDepartmentOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedDepartmentIds}
          onChange={orgCascade.changeDepartments}
          onClose={handleDepartmentIdsCommit}
          placeholder={t('reports.deleted_history.filterDepartmentPlaceholder')}
          className="w-full lg:w-44"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.deleted_history.filterUnit')}
        </Label>
        <MultiSelectCombobox
          options={filteredUnitOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedUnitIds}
          onChange={setSelectedUnitIds}
          onClose={handleUnitIdsCommit}
          placeholder={t('reports.deleted_history.filterUnitPlaceholder')}
          className="w-full lg:w-44"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.deleted_history.fromDate')}
        </Label>
        <DatePickerInput
          value={fromDate}
          onChange={handleFromDateChange}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.deleted_history.toDate')}
        </Label>
        <DatePickerInput
          value={toDate}
          onChange={handleToDateChange}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.deleted_history.filterInstrument')}
        </Label>
        <SearchableSelect
          value={instrumentId}
          onValueChange={handleInstrumentChange}
          options={[
            {
              value: ALL_SENTINEL,
              label: t('reports.deleted_history.filterInstrumentPlaceholder'),
            },
            ...instrumentOptions.map((option) => ({
              value: String(option.id),
              label: option.name,
            })),
          ]}
          placeholder={t('reports.deleted_history.filterInstrumentPlaceholder')}
          className="w-full lg:w-44"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.deleted_history.filterType')}
        </Label>
        <MultiSelectCombobox
          options={recordTypeOptions}
          selected={selectedRecordTypes}
          onChange={setSelectedRecordTypes}
          onClose={handleRecordTypesCommit}
          placeholder={t('reports.deleted_history.filterTypePlaceholder')}
          className="w-full lg:w-44"
          showBadges={false}
        />
      </div>

      <ReportRangeOrderError show={dateRangeInvalid} />
    </div>
  )

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="min-w-0 text-xl font-bold sm:text-2xl">
          {t('reports.deleted_history.title')}
        </h1>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={dateRangeInvalid}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.deleted_history.exportCsv')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPdf}
            disabled={dateRangeInvalid}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.deleted_history.exportPdf')}
          </Button>

          <div className="lg:hidden">
            <Sheet
              onOpenChange={(open) => {
                sheetOpenRef.current = open
                // Prune on close — MultiSelect onClose may not run (e.g. Escape).
                if (!open) closeSheetWithCascadePrune()
              }}
            >
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t('reports.deleted_history.filters')}
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="rounded-t-xl px-4 pt-4 pb-8"
                title={t('reports.deleted_history.filters')}
                hideCloseButton
              >
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="font-semibold">{t('reports.deleted_history.filters')}</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={clearFilters}
                      >
                        {t('reports.deleted_history.clearFilters')}
                      </Button>
                    )}
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('reports.deleted_history.filters')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </SheetClose>
                  </div>
                </div>
                <div className="max-h-[60dvh] overflow-y-auto">{filterFields}</div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {showExportFilterRequired && (
        <p role="alert" className="text-sm text-destructive">
          {t('reports.deleted_history.export.filterRequired')}
        </p>
      )}

      <div className="hidden rounded-md border p-4 lg:block">
        <div className="flex flex-col gap-3">
          {filterFields}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={clearFilters}
              >
                {t('reports.deleted_history.clearFilters')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {displayedRecords.length === 0 ? (
        <ReportEmptyState message={t('reports.deleted_history.noRecords')} />
      ) : (
        <>
          <div className="space-y-2 lg:hidden">
            {displayedRecords.map((history) => (
              <div key={history.id} className="rounded-lg border bg-card p-3 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <RecordTypeBadge recordType={history.recordType} />
                  <p className="min-w-0 flex-1 font-medium [overflow-wrap:anywhere]">
                    {history.instrumentHref ? (
                      <Link href={history.instrumentHref} className="underline">
                        {history.recordName ?? '—'}
                      </Link>
                    ) : (
                      (history.recordName ?? '—')
                    )}
                  </p>
                </div>
                <div className="mt-3 space-y-2">
                  {history.serialNumber && (
                    <ReportMobileField label={t('reports.deleted_history.colSerial')}>
                      {history.serialNumber}
                    </ReportMobileField>
                  )}
                  {history.taskName && (
                    <ReportMobileField label={t('reports.deleted_history.colTask')}>
                      {history.taskName}
                    </ReportMobileField>
                  )}
                  {history.taskCompletedAt && (
                    <ReportMobileField label={t('reports.deleted_history.colTaskCompleted')}>
                      {formatDatetime(history.taskCompletedAt, history.taskCompletedAtZone)}
                    </ReportMobileField>
                  )}
                  <ReportMobileField label={t('reports.deleted_history.colSite')}>
                    {history.siteName ?? '—'}
                  </ReportMobileField>
                  <ReportMobileField label={t('reports.deleted_history.colDepartment')}>
                    {history.departmentName ?? '—'}
                  </ReportMobileField>
                  <ReportMobileField label={t('reports.deleted_history.colDeletedBy')}>
                    {history.deletedByUserName ?? '—'}
                  </ReportMobileField>
                  <ReportMobileField label={t('reports.deleted_history.colDeletedAt')}>
                    {formatDatetime(history.deletedLogAt, history.deletedLogAtZone)}
                  </ReportMobileField>
                  <ReportMobileField label={t('reports.deleted_history.colReason')}>
                    {history.deleteReason || '—'}
                  </ReportMobileField>
                  {history.maintenanceLogsHref && (
                    <Link
                      href={history.maintenanceLogsHref}
                      className="mt-1 block text-xs text-primary underline"
                    >
                      {t('reports.deleted_history.colMaintenanceLogs')}
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="hidden lg:block">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('reports.deleted_history.colType')}</TableHead>
                    <TableHead>{t('reports.deleted_history.colInstrument')}</TableHead>
                    <TableHead>{t('reports.deleted_history.colSerial')}</TableHead>
                    <TableHead>{t('reports.deleted_history.colTask')}</TableHead>
                    <TableHead>{t('reports.deleted_history.colTaskCompleted')}</TableHead>
                    <TableHead>{t('reports.deleted_history.colSite')}</TableHead>
                    <TableHead>{t('reports.deleted_history.colDepartment')}</TableHead>
                    <TableHead>{t('reports.deleted_history.colDeletedBy')}</TableHead>
                    <TableHead>{t('reports.deleted_history.colDeletedAt')}</TableHead>
                    <TableHead>{t('reports.deleted_history.colReason')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedRecords.map((history) => (
                    <TableRow key={history.id}>
                      <TableCell>
                        <RecordTypeBadge recordType={history.recordType} />
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex flex-col gap-0.5">
                          {history.instrumentHref ? (
                            <Link href={history.instrumentHref} className="underline">
                              {history.recordName ?? '—'}
                            </Link>
                          ) : (
                            (history.recordName ?? '—')
                          )}
                          {history.maintenanceLogsHref && (
                            <Link
                              href={history.maintenanceLogsHref}
                              className="text-xs text-muted-foreground underline"
                            >
                              {t('reports.deleted_history.colMaintenanceLogs')}
                            </Link>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {history.serialNumber ?? '—'}
                      </TableCell>
                      <TableCell>{history.taskName ?? '—'}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDatetime(history.taskCompletedAt, history.taskCompletedAtZone)}
                      </TableCell>
                      <TableCell>{history.siteName ?? '—'}</TableCell>
                      <TableCell>{history.departmentName ?? '—'}</TableCell>
                      <TableCell>{history.deletedByUserName ?? '—'}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDatetime(history.deletedLogAt, history.deletedLogAtZone)}
                      </TableCell>
                      <TableCell className="max-w-xs text-sm text-muted-foreground [overflow-wrap:anywhere]">
                        {history.deleteReason || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {currentHasMore && (
            <div className="flex justify-center pt-2">
              <Button variant="outline" onClick={handleLoadMore} disabled={dateRangeInvalid}>
                {t('reports.deleted_history.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default DeletedHistoryPage
